import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { Separator } from 'app/design/primitives/text-divider';

import { useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import type { NavigationScreen } from 'app/types/navigation';
import { isMobileNative, usePageTitle } from 'app/utils/web-helpers';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Linking } from 'react-native';
import { AmbassadorMedal } from 'app/design/icons/ambassador-medal';
import { AmbassadorMedalEmpty } from 'app/design/icons/ambassador-medal-empty';
import { AmbassadorAdvantagesList, AmbassadorLearnMoreButton, AmbassadorMissionSteps } from './ambassador-shared';
import { useAmbassadorSignUpUrl } from './constants';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { useTheme } from 'app/shopify';

const AmbassadorScreenComponent: NavigationScreen<'ambassador'> = ({ navigation }) => {
  const theme = useTheme();
  const { t } = useTranslation('ambassador');
  const url = useAmbassadorSignUpUrl();
  const { isAmbassador, isEnabled } = useAmbassador();

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t('title')}
        </Text>
      ),
    },
    false,
  );

  const setPageTitle = usePageTitle('ambassador');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  if (!isEnabled) return null;

  return (
    <Page>
      <Column alignItems="center" gap="spacing.general.x4" width="100%">
        {isAmbassador ? (
          <AmbassadorMedal
            height={190}
            width={180}
            style={
              isMobileNative
                ? {
                    shadowColor: theme.colors['text.brand'],
                    shadowOffset: { width: 0, height: 10 },
                    shadowOpacity: 0.5,
                    shadowRadius: 6,
                  }
                : undefined
            }
          />
        ) : (
          <AmbassadorMedalEmpty height={168} width={145} />
        )}

        <Text textAlign="center" variant="headerXSmall">
          <Trans
            components={{ strong: <Text fontWeight="700" /> }}
            i18nKey={isAmbassador ? 'subtitle.isAmbassador' : 'subtitle.notYetAmbassador'}
            ns="ambassador"
          />
        </Text>

        <AmbassadorDescription key={String(isAmbassador)} isAmbassador={isAmbassador} />

        {!isAmbassador && (
          <Button
            onPress={() => void Linking.openURL(url).catch((error: unknown) => console.error(error))}
            style={{ width: '100%', borderRadius: theme.borderRadii['radius.xl'] }}
            variant="primary"
          >
            <Text color="text.inverse.primary" fontWeight="700" variant="bodyBase">
              {t('ctaButton')} ↗
            </Text>
          </Button>
        )}

        {/* {isAmbassador ? <AmbassadorChannels navigation={navigation} /> : null} */}

        <Separator style={{ width: '100%', marginTop: theme.spacing['spacing.general.x4'] }} />

        <Column gap="spacing.general.x2" width="100%">
          <Text fontWeight="700" variant="bodyLarge">
            {t(isAmbassador ? 'advantages.titleIsAmbassador' : 'advantages.titleNotYet')}
          </Text>
          <AmbassadorAdvantagesList listGap="spacing.general.x1" rowGap="spacing.general.x4" />
        </Column>

        <Separator style={{ width: '100%', marginTop: theme.spacing['spacing.general.x4'] }} />

        <Column gap="spacing.general.x4" width="100%">
          <Text fontWeight="700" variant="bodyLarge">
            {t('mission.title')}
          </Text>
          <Text variant="bodyBase">
            <Trans components={{ strong: <Text fontWeight="700" /> }} i18nKey="mission.description" ns="ambassador" />
          </Text>
          <AmbassadorMissionSteps />
        </Column>

        <AmbassadorLearnMoreButton />
      </Column>
    </Page>
  );
};

export const AmbassadorScreen: NavigationScreen<'ambassador'> = withOptionalMetrics(
  'AmbassadorScreen',
  withErrorBoundary(withDashboardLayout(AmbassadorScreenComponent)),
);

// ─── Description with expand/collapse ────────────────────────────────────────

interface TextSegment {
  id: string;
  text: string;
  bold: boolean;
}

function parseSegments(raw: string): TextSegment[] {
  const segments: TextSegment[] = [];
  const regex = /<strong>(.*?)<\/strong>/gs;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ id: `plain-${String(lastIndex)}`, text: raw.slice(lastIndex, match.index), bold: false });
    }
    const matchText = match[1];
    if (matchText) segments.push({ id: `bold-${String(match.index)}`, text: matchText, bold: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < raw.length) {
    segments.push({ id: `plain-${String(lastIndex)}`, text: raw.slice(lastIndex), bold: false });
  }
  return segments;
}

function truncateSegments(segments: TextSegment[], maxChars: number): TextSegment[] {
  const result: TextSegment[] = [];
  let remaining = maxChars;
  for (const seg of segments) {
    if (remaining <= 0) break;
    if (seg.text.length <= remaining) {
      result.push(seg);
      remaining -= seg.text.length;
    } else {
      result.push({ ...seg, text: seg.text.slice(0, remaining) });
      remaining = 0;
    }
  }
  return result;
}

function AmbassadorDescription({ isAmbassador }: { isAmbassador: boolean }) {
  const { t } = useTranslation('ambassador');
  const [expansionOverride, setExpansionOverride] = useState<boolean>();
  const expanded = expansionOverride ?? isAmbassador;

  const descKey = isAmbassador ? 'description.isAmbassador' : 'description.notYetAmbassador';
  const rawText: string = t(descKey);
  const plainText = rawText.replace(/<[^>]+>/g, '');
  const truncateAt = 110;
  const isTruncatable = plainText.length > truncateAt;
  const segments = parseSegments(rawText);
  const truncatedSegments = truncateSegments(segments, truncateAt);

  return (
    <Column
      gap="spacing.general.x1"
      backgroundColor="surface.tertiary-neutral"
      padding="spacing.general.x2"
      borderRadius="radius.sm"
    >
      <Text variant="bodyBase" textAlign="auto" letterSpacing={-0.2}>
        {isTruncatable && !expanded ? (
          <>
            {truncatedSegments.map((seg) =>
              seg.bold ? (
                <Text key={seg.id} fontWeight="700">
                  {seg.text}
                </Text>
              ) : (
                seg.text
              ),
            )}
            {'… '}
            <Text color="text.interactive" onPress={() => setExpansionOverride(true)} variant="bodyBase">
              {t('showMore')}
            </Text>
          </>
        ) : (
          <>
            <Trans
              components={{ strong: <Text fontWeight="700" /> }}
              i18nKey={descKey}
              ns="ambassador"
              style={{ letterSpacing: -0.2 }}
            />
            {isTruncatable ? (
              <>
                {' '}
                <Text color="text.interactive" onPress={() => setExpansionOverride(false)} variant="bodyBase">
                  {t('showLess')}
                </Text>
              </>
            ) : null}
          </>
        )}
      </Text>
    </Column>
  );
}

// ─── Channels (ambassador only) ──────────────────────────────────────────────
// @TODO: Implement this for a v2 of the in-app ambassador program.
// function AmbassadorChannels({ navigation }: { navigation: SpecificNavigationProp<'ambassador'> }) {
//   const { t } = useTranslation('ambassador');
//   const channelsQuery = useAllChannels({ userId: undefined }, {});
//   const channels =
//     channelsQuery.data?.pages.flatMap((page) => page.results).filter((item) => !isGroupHeader(item)) ?? []; // && item.custom_type === CustomChannelType.Ambassador) ?? [];

//   if (!channels.length) return null;

//   return (
//     <Column gap="s" width="100%">
//       {channels.map((channel) => (
//         <Pressable key={channel.channel_url} onPress={() => navigateToChat(navigation, channel.channel_url)}>
//           <Row
//             alignItems="center"
//             backgroundColor="background"
//             borderRadius="m"
//             gap="m"
//             padding="m"
//             style={{ borderWidth: 1, borderColor: theme.colors['neutral.light'] }}
//           >
//             <Thumbnail height={48} source={{ uri: channel.picture }} width={48} />
//             <Column flex={1} gap="xs">
//               <Text fontWeight="700" numberOfLines={1} variant="h3">
//                 {channel.name}
//               </Text>
//               <Row alignItems="center" gap="xs">
//                 <Text fontSize={13} color="grey-secondary">
//                   {t('channelDiscussion')}
//                 </Text>
//               </Row>
//               {channel.members
//                 ?.slice(0, 3)
//                 .map((m) => m.profile_url)
//                 .filter(Boolean).length > 0 && (
//                 <Row gap="xs">
//                   {channel.members.slice(0, 3).map((member) => (
//                     <Thumbnail key={member.user_id} height={24} source={{ uri: member.profile_url }} width={24} />
//                   ))}
//                 </Row>
//               )}
//             </Column>
//             <IconChevronRight />
//           </Row>
//         </Pressable>
//       ))}
//     </Column>
//   );
// }
